import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapsApi } from './MapsApiProvider';

export interface PlaceAutocompleteSuggestion {
  id: string;
  mainText: string;
  secondaryText: string;
  raw: google.maps.places.AutocompleteSuggestion;
}

interface UsePlacesAutocompleteOptions {
  debounceMs?: number;
  includedPrimaryTypes?: string[];
  language?: string;
}

interface UsePlacesAutocompleteReturn {
  input: string;
  setQuery: (query: string) => void;
  suggestions: PlaceAutocompleteSuggestion[];
  open: boolean;
  setOpen: (open: boolean) => void;
  loading: boolean;
  select: (
    suggestion: PlaceAutocompleteSuggestion
  ) => Promise<google.maps.places.Place | undefined>;
}

export const usePlacesAutocomplete = (
  options: UsePlacesAutocompleteOptions = {}
): UsePlacesAutocompleteReturn => {
  const { isReady } = useMapsApi();
  const { debounceMs = 150, includedPrimaryTypes, language } = options;

  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceAutocompleteSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setQuery = useCallback((query: string) => {
    setInput(query);
  }, []);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (!isReady || !query.trim()) {
        setSuggestions([]);
        return;
      }

      setLoading(true);

      try {
        // Import the places library dynamically
        const { AutocompleteSuggestion } = (await google.maps.importLibrary(
          'places'
        )) as google.maps.PlacesLibrary;

        // Create session token if not exists
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        }

        const request = {
          input: query,
          sessionToken: sessionTokenRef.current,
          ...(includedPrimaryTypes && { includedPrimaryTypes }),
          ...(language && { language }),
        };

        const { suggestions: rawSuggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        const mappedSuggestions: PlaceAutocompleteSuggestion[] = rawSuggestions.map(suggestion => {
          const placePrediction = suggestion.placePrediction;

          // Type assertion for extended PlacePrediction properties not yet in @types/google.maps
          const extendedPrediction = placePrediction as google.maps.places.PlacePrediction & {
            structuredFormat?: {
              mainText?: { text: string };
              secondaryText?: { text: string };
            };
            text?: { text: string };
          };

          return {
            id: placePrediction?.placeId || '',
            mainText:
              extendedPrediction?.structuredFormat?.mainText?.text ||
              extendedPrediction?.text?.text ||
              '',
            secondaryText: extendedPrediction?.structuredFormat?.secondaryText?.text || '',
            raw: suggestion,
          };
        });

        setSuggestions(mappedSuggestions);
      } catch (error) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [isReady, includedPrimaryTypes, language]
  );

  // Debounced input effect
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (input.trim()) {
        fetchSuggestions(input);
        setOpen(true);
      } else {
        setSuggestions([]);
        setOpen(false);
      }
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [input, fetchSuggestions, debounceMs]);

  const select = useCallback(
    async (
      suggestion: PlaceAutocompleteSuggestion
    ): Promise<google.maps.places.Place | undefined> => {
      if (!suggestion.raw.placePrediction) {
        return undefined;
      }

      // Convert prediction to place
      const place = suggestion.raw.placePrediction.toPlace();

      // Fetch detailed fields
      await place.fetchFields({
        fields: ['id', 'displayName', 'formattedAddress', 'location', 'addressComponents'],
      });

      // End session after first details fetch
      sessionTokenRef.current = null;

      return place;
    },
    []
  );

  return {
    input,
    setQuery,
    suggestions,
    open,
    setOpen,
    loading,
    select,
  };
};
